export interface VotingSettings {
  sourceKind: 'manual' | 'hearing-panel';
  hearingReferenceField?: string;
  votingRolesJson?: string;
  populationNodeId?: string;
  roundNodeId?: string;
  question?: string;
  captureMode: 'personal' | 'recorded' | 'both';
  rulesJson?: string;
}
export interface VotingRules {
  quorumThreshold: number;
  approvalRuleCode: string;
  denominatorCode: string;
  abstentionQuorumCode: string;
  tieCode: string;
}
export interface VotingMember {
  key: string;
  displayName: string;
  roleCode: string;
  eligible: boolean;
  exclusionReason: string | null;
  userId: number | null;
  participantId: number | null;
  panelAssignmentId?: number | null;
  hasVoted?: boolean;
  choice?: string | null;
  voteRevision?: number;
  canVote?: boolean;
  captureMode?: string | null;
}
export interface VotingResult {
  eligibleCount: number;
  participatingCount: number;
  approveCount: number;
  rejectCount: number;
  abstainCount: number;
  missingCount: number;
  voteDenominator: number;
  requiredApprovals: number | null;
  quorumSatisfied: boolean;
  outcomeCode: string;
  resultHash: string;
}
export interface VotingWorkspace {
  occurrenceId: number;
  workflowId: number;
  nodeId: string;
  code: string;
  settings: VotingSettings;
  rules?: VotingRules | null;
  sourceHash: string;
  sourceCurrent: boolean;
  issue: string;
  populationId: number;
  expectedPopulationId: number;
  populationRevision: number;
  confirmed: boolean;
  roundId: number;
  roundNumber: number;
  roundVersion: number;
  roundStatus: string;
  question: string | null;
  ballotRevision: number;
  result: VotingResult | null;
  evaluated: boolean;
  closeRecordsResult?: boolean;
  requiresAllVotes?: boolean;
  canManage: boolean;
  canEvaluate: boolean;
  canRecord: boolean;
  canCast: boolean;
  canAudit: boolean;
  userId: number;
  eTag: string;
  unresolved: { id: number; displayName: string; roleCode: string }[];
  hearingPanelUrl?: string | null;
  members: VotingMember[];
}
export const votingCodes = [
  'voting.population',
  'voting.ballots',
  'voting.result',
] as const;
export const votingOperations: Record<string, string[]> = {
  'voting.population': [
    'save-population',
    'confirm-population',
    'identify-panel-member',
  ],
  'voting.ballots': [
    'open-round',
    'cast',
    'record',
    'close-round',
    'restart-round',
  ],
  'voting.result': ['evaluate'],
};
export const VOTING_POPULATION = 'voting.population-confirmed';
export const VOTING_ROUND = 'voting.round-completed';
export function isVotingRequirement(
  provider: string,
  version: number,
): boolean {
  return (
    ([VOTING_POPULATION, VOTING_ROUND].includes(provider) && version === 1) ||
    (provider === 'workflow.quorum-evaluated' && version === 2)
  );
}
export const votingRoleChoices = [
  ['presiding', 'Presiding'],
  ['presiding_officer', 'Presiding officer'],
  ['deputy', 'Deputy'],
  ['deputy_commissioner', 'Deputy commissioner'],
  ['attorney', 'Attorney'],
  ['deputy_attorney', 'Deputy attorney'],
  ['attorney_general', 'Attorney general'],
  ['hearing_officer', 'Hearing officer'],
  ['hearingofficer', 'Hearing officer (legacy)'],
  ['clerk', 'Clerk'],
  ['hearing_clerk', 'Hearing clerk'],
  ['member', 'Member'],
  ['panel_member', 'Panel member'],
] as const;
